import { useState, useEffect } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const HexLogo = () => (
  <svg width="32" height="36" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="hexGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7C3AED" />
        <stop offset="100%" stopColor="#BF7FFF" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <path 
      d="M16 0L31.5 8.5V27.5L16 36L0.5 27.5V8.5L16 0Z" 
      fill="url(#hexGradient)"
      opacity="0.3"
    />
    <path 
      d="M16 4L27.5 10.5V25.5L16 32L4.5 25.5V10.5L16 4Z" 
      fill="url(#hexGradient)"
      opacity="0.5"
    />
    <path 
      d="M16 8L23.5 12.5V23.5L16 28L8.5 23.5V12.5L16 8Z" 
      fill="url(#hexGradient)"
    />
    <circle cx="16" cy="18" r="3" fill="white" filter="url(#glow)" />
  </svg>
);

const Navbar = ({ cartItemCount, onCartClick, onNavClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "How It Works", action: onNavClick.howItWorks },
    { label: "Formula", action: onNavClick.formula },
    { label: "Shop", action: onNavClick.shop },
    { label: "Reviews", action: onNavClick.reviews },
    { label: "FAQ", action: onNavClick.faq }
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-nav shadow-lg" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <HexLogo />
            <span className="text-white font-bold text-lg tracking-tight">NUKLEUS</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={link.action}
                className="text-sm text-white/60 hover:text-white transition-colors"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            <span className="hidden lg:flex items-center text-xs text-white/50 bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
              Free US shipping
            </span>
            
            <Button
              onClick={onNavClick.shop}
              className="hidden sm:flex bg-nukleus-violet hover:bg-nukleus-violet/90 text-white rounded-full px-5 py-2 text-sm font-medium shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] transition-all"
              data-testid="shop-now-btn"
            >
              Shop Now
            </Button>

            <button
              onClick={onCartClick}
              className="relative p-2 text-white/70 hover:text-white transition-colors"
              data-testid="cart-btn"
            >
              <ShoppingBag className="w-5 h-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-nukleus-violet text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {cartItemCount}
                </span>
              )}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-white/70 hover:text-white transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden glass-nav border-t border-white/10" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => {
                  link.action();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left text-white/70 hover:text-white py-2 transition-colors"
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => {
                onNavClick.shop();
                setIsMobileMenuOpen(false);
              }}
              className="w-full bg-nukleus-violet hover:bg-nukleus-violet/90 text-white rounded-full mt-2"
            >
              Shop Now
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
